import React from 'react';
import { ArrowUpIcon, ArrowDownIcon, EyeIcon, EditIcon, CheckIcon, XIcon, FlagIcon, BuildingIcon, HomeIcon } from 'lucide-react';
import { ExchangeRate } from '../CurrencyExchangeView';
import { ExchangeRateBadge } from './ExchangeRateBadge';
interface ExchangeRateTableProps {
  rates: ExchangeRate[];
  onEdit: (rate: ExchangeRate) => void;
  onToggleStatus: (rateId: string) => void;
  onViewHistory: (rate: ExchangeRate) => void;
  locale: string;
}
export function ExchangeRateTable({
  rates,
  onEdit,
  onToggleStatus,
  onViewHistory,
  locale
}: ExchangeRateTableProps) {
  // Format date based on locale
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };
  // Get flag emoji for currency
  const getCurrencyFlag = (currencyCode: string) => {
    const flagMap: Record<string, string> = {
      USD: '🇺🇸',
      EUR: '🇪🇺',
      GBP: '🇬🇧',
      CAD: '🇨🇦',
      AUD: '🇦🇺',
      JPY: '🇯🇵',
      CNY: '🇨🇳',
      INR: '🇮🇳',
      BRL: '🇧🇷',
      MXN: '🇲🇽',
      ZAR: '🇿🇦',
      RUB: '🇷🇺',
      SGD: '🇸🇬'
      // Add more as needed
    };
    return flagMap[currencyCode] || '🌐';
  };
  // Get scope icon
  const getScopeIcon = (rate: ExchangeRate) => {
    switch (rate.scope) {
      case 'Global':
        return <FlagIcon className="h-4 w-4 text-blue-500" title="Global" data-id="element-7786" />;
      case 'Entity':
        return <BuildingIcon className="h-4 w-4 text-purple-500" title={rate.entityName || 'Entity'} data-id="element-7787" />;
      case 'Site':
        return <HomeIcon className="h-4 w-4 text-green-500" title={rate.siteName || 'Site'} data-id="element-7788" />;
      default:
        return null;
    }
  };
  // Get scope label
  const getScopeLabel = (rate: ExchangeRate) => {
    switch (rate.scope) {
      case 'Global':
        return 'Global';
      case 'Entity':
        return rate.entityName || 'Entity';
      case 'Site':
        return rate.siteName || 'Site';
      default:
        return '';
    }
  };
  return <div className="overflow-x-auto" data-id="element-7789">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-7790">
        <thead className="bg-gray-50" data-id="element-7791">
          <tr data-id="element-7792">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7793">
              Currency Pair
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7794">
              Rate
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7795">
              Type
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7796">
              Scope
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7797">
              Last Updated
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7798">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7799">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-7800">
          {rates.length === 0 ? <tr data-id="element-7801">
              <td colSpan={7} className="px-6 py-4 text-center text-gray-500" data-id="element-7802">
                No exchange rates found matching your criteria.
              </td>
            </tr> : rates.map(rate => <tr key={rate.id} className={!rate.isActive ? 'bg-gray-50' : undefined} data-id="element-7803">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-7804">
                  <div className="flex items-center" data-id="element-7805">
                    <span className="mr-1" data-id="element-7806">
                      {getCurrencyFlag(rate.fromCurrency)}
                    </span>
                    <span className="font-medium" data-id="element-7807">{rate.fromCurrency}</span>
                    <ArrowRightIcon className="h-4 w-4 mx-1 text-gray-400" data-id="element-7808" />
                    <span className="mr-1" data-id="element-7809">
                      {getCurrencyFlag(rate.toCurrency)}
                    </span>
                    <span className="font-medium" data-id="element-7810">{rate.toCurrency}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-7811">
                  <div className="text-sm" data-id="element-7812">
                    <div className="font-medium text-gray-900" data-id="element-7813">
                      1 {rate.fromCurrency} = {rate.rate.toFixed(4)}{' '}
                      {rate.toCurrency}
                    </div>
                    <div className="text-gray-500 text-xs" data-id="element-7814">
                      1 {rate.toCurrency} = {rate.inverseRate.toFixed(4)}{' '}
                      {rate.fromCurrency}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-7815">
                  <ExchangeRateBadge type={rate.type} data-id="element-7816" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-7817">
                  <div className="flex items-center" data-id="element-7818">
                    {getScopeIcon(rate)}
                    <span className="ml-1 text-sm text-gray-900" data-id="element-7819">
                      {getScopeLabel(rate)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-7820">
                  <div className="text-sm text-gray-900" data-id="element-7821">
                    {formatDate(rate.lastUpdated)}
                  </div>
                  {rate.updatedBy && <div className="text-xs text-gray-500" data-id="element-7822">
                      {rate.updatedBy}
                    </div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-7823">
                  {rate.isActive ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-7824">
                      <CheckIcon className="h-3 w-3 mr-1" data-id="element-7825" />
                      Active
                    </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-7826">
                      <XIcon className="h-3 w-3 mr-1" data-id="element-7827" />
                      Inactive
                    </span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-7828">
                  <div className="flex justify-end space-x-2" data-id="element-7829">
                    <button onClick={() => onViewHistory(rate)} className="text-indigo-600 hover:text-indigo-900" title="View History" data-id="element-7830">
                      <EyeIcon className="h-4 w-4" data-id="element-7831" />
                    </button>
                    <button onClick={() => onEdit(rate)} className="text-blue-600 hover:text-blue-900" title="Edit" data-id="element-7832">
                      <EditIcon className="h-4 w-4" data-id="element-7833" />
                    </button>
                    <button onClick={() => onToggleStatus(rate.id)} className={rate.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'} title={rate.isActive ? 'Deactivate' : 'Activate'} data-id="element-7834">
                      {rate.isActive ? <XIcon className="h-4 w-4" data-id="element-7835" /> : <CheckIcon className="h-4 w-4" data-id="element-7836" />}
                    </button>
                  </div>
                </td>
              </tr>)}
        </tbody>
      </table>
    </div>;
}
// Helper component for the arrow icon
const ArrowRightIcon = ({
  className
}: {
  className?: string;
}) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-7837">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" data-id="element-7838" />
  </svg>;